import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  notesService,
  type ApiNote,
  type MentionableUser,
  type NoteMention,
} from "@/services/notesService";
import { useScopeParams } from "./useScopeParams";

export const entityNotesQueryKey = (
  scope: string,
  id: string,
  entityKey: string,
) => ["entity-notes", scope, id, entityKey] as const;

/**
 * Server-backed notes for one entity.
 *
 * The read and every write go through the same `(scope, id, entityKey)` triple,
 * so a note created here is the same note the next visit lists.
 */
export function useEntityNotes(entityKey: string) {
  const { scope, id } = useScopeParams();
  const queryClient = useQueryClient();
  const queryKey = entityNotesQueryKey(scope, id, entityKey);

  const enabled = Boolean(scope && id && entityKey);

  const query = useQuery({
    queryKey,
    queryFn: () => notesService.listNotes(scope, id, entityKey),
    enabled,
    staleTime: 30 * 1000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  // Who the author may tag. Trial-scoped, not entity-scoped, so every Notes tab
  // in one trial shares the cached list.
  const mentionableQuery = useQuery({
    queryKey: ["mentionable-users", scope, id] as const,
    queryFn: () => notesService.listMentionableUsers(scope, id),
    enabled: Boolean(scope && id),
    staleTime: 5 * 60 * 1000,
  });
  const mentionableUsers = (mentionableQuery.data ?? []) as MentionableUser[];
  const mentionableUsersLoading = mentionableQuery.isLoading;
  const mentionableUsersFailed = Boolean(mentionableQuery.error);

  const createNote = useMutation({
    mutationFn: (payload: {
      text: string;
      trial_tag?: string | null;
      mentions?: NoteMention[];
    }) => notesService.createNote(scope, id, entityKey, payload),
    onSuccess: invalidate,
  });

  const updateNote = useMutation({
    mutationFn: ({
      noteId,
      text,
      mentions,
    }: {
      noteId: number;
      text: string;
      mentions?: NoteMention[];
    }) =>
      notesService.updateNote(scope, id, entityKey, noteId, { text, mentions }),
    onSuccess: invalidate,
  });

  const deleteNote = useMutation({
    mutationFn: (noteId: number) =>
      notesService.deleteNote(scope, id, entityKey, noteId),
    onSuccess: invalidate,
  });

  return {
    notes: (query.data ?? []) as ApiNote[],
    mentionableUsers,
    mentionableUsersLoading,
    mentionableUsersFailed,
    isLoading: query.isLoading,
    error: query.error,
    createNote,
    updateNote,
    deleteNote,
  };
}
